import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCar, 
    faPlus, 
    faSave, 
    faTimes, 
    faSpinner, 
    faCheckCircle, 
    faExclamationTriangle,
    faTrashAlt,
    faList,
    faCalendar,
    faHashtag,
    faUsers,
    faDownload,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import "./Styles/Form.css";

// Translator instance
const createCarTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_car_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_car_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Add Cars': 'کاریں شامل کریں',
                'Enter car details below': 'نیچے کار کی تفصیلات درج کریں',
                'Serial Number': 'سیریل نمبر',
                'Car Name': 'کار کا نام',
                'Date': 'تاریخ',
                'Add Car': 'کار شامل کریں',
                'Remove': 'ہٹائیں',
                'Save All Cars': 'تمام کاریں محفوظ کریں',
                'Save': 'محفوظ کریں',
                'Clear All': 'سب صاف کریں',
                'Saving...': 'محفوظ کر رہے ہیں...',
                'Cars saved successfully!': 'کاریں کامیابی سے محفوظ ہوگئیں!',
                'Failed to save cars': 'کاریں محفوظ کرنے میں ناکامی',
                'Network error occurred': 'نیٹ ورک کی خرابی',
                'Car name is required': 'کار کا نام ضروری ہے',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Select a shop first': 'پہلے ایک شاپ منتخب کریں',
                'No cars added yet': 'ابھی تک کوئی کار شامل نہیں کی گئی',
                'Click + to add cars': 'کاریں شامل کرنے کے لیے + پر کلک کریں',
                'Total Cars': 'کل کاریں',
                'Duplicate serial number detected': 'نقل سیریل نمبر پایا گیا',
                'Export CSV': 'CSV ایکسپورٹ کریں',
                'Import CSV': 'CSV امپورٹ کریں',
                'Switch Shop': 'شاپ تبدیل کریں',
                'Loading shops...': 'شاپس لوڈ ہو رہی ہیں...',
                'No shops available': 'کوئی شاپ دستیاب نہیں',
                'Current': 'موجودہ',
                'Shop switched successfully': 'شاپ کامیابی سے تبدیل ہوگئی',
                'Failed to switch shop': 'شاپ تبدیل کرنے میں ناکامی'
            },
            'ar': {
                'Add Cars': 'إضافة سيارات',
                'Enter car details below': 'أدخل تفاصيل السيارة أدناه',
                'Serial Number': 'الرقم التسلسلي',
                'Car Name': 'اسم السيارة',
                'Date': 'التاريخ',
                'Add Car': 'إضافة سيارة',
                'Remove': 'إزالة',
                'Save All Cars': 'حفظ جميع السيارات',
                'Save': 'حفظ',
                'Clear All': 'مسح الكل',
                'Saving...': 'جاري الحفظ...',
                'Cars saved successfully!': 'تم حفظ السيارات بنجاح!',
                'Failed to save cars': 'فشل في حفظ السيارات',
                'Network error occurred': 'حدث خطأ في الشبكة',
                'Car name is required': 'اسم السيارة مطلوب',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Select a shop first': 'اختر متجرًا أولاً',
                'No cars added yet': 'لم يتم إضافة سيارات بعد',
                'Click + to add cars': 'انقر + لإضافة سيارات',
                'Total Cars': 'إجمالي السيارات',
                'Duplicate serial number detected': 'تم اكتشاف رقم تسلسلي مكرر',
                'Export CSV': 'تصدير CSV',
                'Import CSV': 'استيراد CSV',
                'Switch Shop': 'تبديل المتجر',
                'Loading shops...': 'جاري تحميل المتاجر...',
                'No shops available': 'لا توجد متاجر متاحة',
                'Current': 'الحالي',
                'Shop switched successfully': 'تم تبديل المتجر بنجاح',
                'Failed to switch shop': 'فشل في تبديل المتجر'
            },
            'es': {
                'Add Cars': 'Agregar Autos',
                'Enter car details below': 'Ingrese los detalles del auto a continuación',
                'Serial Number': 'Número de Serie',
                'Car Name': 'Nombre del Auto',
                'Date': 'Fecha',
                'Add Car': 'Agregar Auto',
                'Remove': 'Eliminar',
                'Save All Cars': 'Guardar Todos los Autos',
                'Save': 'Guardar',
                'Clear All': 'Limpiar Todo',
                'Saving...': 'Guardando...',
                'Cars saved successfully!': '¡Autos guardados exitosamente!',
                'Failed to save cars': 'Error al guardar los autos',
                'Network error occurred': 'Ocurrió un error de red',
                'Car name is required': 'El nombre del auto es requerido',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Select a shop first': 'Seleccione una tienda primero',
                'No cars added yet': 'No se han agregado autos aún',
                'Click + to add cars': 'Haga clic en + para agregar autos',
                'Total Cars': 'Total de Autos',
                'Duplicate serial number detected': 'Número de serie duplicado detectado',
                'Export CSV': 'Exportar CSV',
                'Import CSV': 'Importar CSV',
                'Switch Shop': 'Cambiar Tienda',
                'Loading shops...': 'Cargando tiendas...',
                'No shops available': 'No hay tiendas disponibles',
                'Current': 'Actual',
                'Shop switched successfully': 'Tienda cambiada exitosamente',
                'Failed to switch shop': 'Error al cambiar la tienda'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("Translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addCars: 'Add Cars',
        enterDetails: 'Enter car details below',
        serialNumber: 'Serial Number',
        carName: 'Car Name',
        date: 'Date',
        addCar: 'Add Car',
        remove: 'Remove',
        saveAll: 'Save All Cars',
        save: 'Save',
        clearAll: 'Clear All',
        saving: 'Saving...',
        carsSaved: 'Cars saved successfully!',
        failedSave: 'Failed to save cars',
        networkError: 'Network error occurred',
        carNameRequired: 'Car name is required',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        noCars: 'No cars added yet',
        clickToAdd: 'Click + to add cars',
        totalCars: 'Total Cars',
        duplicateSerial: 'Duplicate serial number detected',
        exportCSV: 'Export CSV',
        importCSV: 'Import CSV',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const carTranslator = createCarTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

const CarForm = () => {
    const { user, currentShop, switchShop } = useAuth(); // Get switchShop from useAuth here
    
    const [cars, setCars] = useState([
        { id: Date.now(), serial_number: '', car_name: '', date: new Date().toISOString().split('T')[0] }
    ]);
    
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const nextIdRef = useRef(Date.now());

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // Theme application
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('dynamic-orb-styles');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // Dark mode sync
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                setDarkMode(event.detail.darkMode);
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // Language sync
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await carTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                setCurrentLanguage(event.detail.language);
                loadTranslations(event.detail.language);
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
        };
    }, [currentLanguage]);

    // RTL direction sync
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/get_shops_for_switch.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: user.username })
                });
                const data = await response.json();
                setAvailableShops(data.shops || []);
            } catch (error) {
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user]);

    const addCarEntry = () => {
        const newCar = {
            id: nextIdRef.current++,
            serial_number: '',
            car_name: '',
            date: new Date().toISOString().split('T')[0]
        };
        setCars([...cars, newCar]);
    };

    const removeCarEntry = (id) => {
        if (cars.length === 1) {
            showToast('warning', 'Cannot remove the last entry');
            return;
        }
        setCars(cars.filter(car => car.id !== id));
    };

    const updateCarField = (id, field, value) => {
        setCars(cars.map(car => 
            car.id === id ? { ...car, [field]: value } : car
        ));
    };

    const getDuplicateSerialNumbers = () => {
        const serials = cars.map(car => car.serial_number).filter(s => s.trim() !== '');
        const duplicates = serials.filter((serial, index) => serials.indexOf(serial) !== index);
        return [...new Set(duplicates)];
    };

    const validateCars = () => {
        const errors = [];
        const duplicateSerials = getDuplicateSerialNumbers();
        
        if (duplicateSerials.length > 0) {
            errors.push(`Duplicate serial numbers: ${duplicateSerials.join(', ')}`);
        }
        
        cars.forEach((car, index) => {
            if (!car.car_name.trim()) {
                errors.push(`Row ${index + 1}: Car name is required`);
            }
        });
        
        return errors;
    };

    const saveAllCars = async () => {
        if (isSubmittingRef.current) return;
        
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        
        const validationErrors = validateCars();
        if (validationErrors.length > 0) {
            showToast('error', validationErrors.join(', '));
            return;
        }
        
        const carsToSave = cars.filter(car => car.car_name.trim() !== '');
        
        if (carsToSave.length === 0) {
            showToast('warning', translations.carNameRequired || 'At least one car name is required');
            return;
        }
        
        setLoading(true);
        isSubmittingRef.current = true;
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_cars.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_multiple',
                    cars: carsToSave.map(car => ({
                        serial_number: car.serial_number,
                        car_name: car.car_name,
                        date: car.date
                    })),
                    username: user?.username,
                    shop_id: currentShop.shop_id,
                    track_batch: true
                })
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', `${data.message}`);
                setCars([{ 
                    id: nextIdRef.current++, 
                    serial_number: '', 
                    car_name: '', 
                    date: new Date().toISOString().split('T')[0] 
                }]);
            } else {
                showToast('error', data.message || translations.failedSave || 'Failed to save cars');
            }
        } catch (error) {
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
            setTimeout(() => {
                isSubmittingRef.current = false;
            }, 500);
        }
    };

    const clearAllCars = () => {
        if (window.confirm('Are you sure you want to clear all car entries?')) {
            setCars([{ 
                id: nextIdRef.current++, 
                serial_number: '', 
                car_name: '', 
                date: new Date().toISOString().split('T')[0] 
            }]);
            showToast('success', 'Form cleared');
        }
    };

    const exportToCSV = () => {
        const headers = ['Serial Number', 'Car Name', 'Date'];
        const rows = cars.map(car => [
            car.serial_number,
            car.car_name,
            car.date
        ]);
        
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `cars_${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
        URL.revokeObjectURL(url);
        
        showToast('success', 'CSV exported successfully');
    };

    const importFromCSV = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split('\n');
            const startIndex = lines[0].toLowerCase().includes('serial') ? 1 : 0;
            
            const newCars = [];
            let currentId = nextIdRef.current;
            
            for (let i = startIndex; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                
                const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
                if (matches) {
                    const values = matches.map(m => m.replace(/^"|"$/g, '').trim());
                    const serialNumber = values[0] || '';
                    const carName = values[1] || '';
                    const date = values[2] || new Date().toISOString().split('T')[0];
                    
                    if (carName) {
                        newCars.push({
                            id: currentId++,
                            serial_number: serialNumber,
                            car_name: carName,
                            date: date
                        });
                    }
                }
            }
            
            if (newCars.length > 0) {
                setCars(newCars);
                nextIdRef.current = currentId;
                showToast('success', `${newCars.length} cars imported successfully`);
            } else {
                showToast('warning', 'No valid car data found in CSV');
            }
        };
        
        reader.readAsText(file);
        event.target.value = '';
    };

    const duplicateSerials = getDuplicateSerialNumbers();
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const totalCars = cars.filter(c => c.car_name.trim() !== '').length;

    // FIXED: handleSwitchShop now uses the switchShop from useAuth (already available at component level)
    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                setShowShopSelector(false);
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    }, [switchShop, showToast, translations]);

    // Shop Selector Modal Component (defined inside CarForm to use translations)
    const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
        if (!isOpen) return null;

        return (
            <div className="ozui_modal-overlay" onClick={onClose}>
                <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                    <div className="ozui_modal-header">
                        <h3>{translations.switchShop || 'Switch Shop'}</h3>
                        <button className="ozui_modal-close" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="ozui_modal-body">
                        {loading ? (
                            <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                        ) : shops.length === 0 ? (
                            <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                        ) : (
                            shops.map((shop) => (
                                <div 
                                    key={shop.shop_id}
                                    className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozui_shop-info">
                                        <div className="ozui_shop-name">{shop.shop_name}</div>
                                        <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>
                                <FontAwesomeIcon icon={faCar} />
                                {translations.addCars || 'Add Cars'}
                            </h1>
                            <p>{translations.enterDetails || 'Enter car details below'}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        {!currentShop && (
                            <button className="ozui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faUsers} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                        
                        <div className="ozui-header-actions">
                            <button 
                                className="ozui-action-btn" 
                                onClick={exportToCSV}
                                disabled={loading}
                                title={translations.exportCSV || 'Export CSV'}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </button>
                            
                            <label className="ozui-action-btn" title={translations.importCSV || 'Import CSV'}>
                                <FontAwesomeIcon icon={faUpload} />
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={importFromCSV}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            
                            <button 
                                className="ozui-action-btn" 
                                onClick={clearAllCars}
                                disabled={loading}
                                title={translations.clearAll || 'Clear All'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            
                            <div className="ozui-cars-counter">
                                <FontAwesomeIcon icon={faList} />
                                <span>{translations.totalCars || 'Total Cars'}: {totalCars}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form className="ozui-form">
                <div className="ozui-form-row">
                    <div className="ozui-cars-table-container">
                        <table className="ozui-cars-table">
                            <thead>
                                <tr>
                                    <th className="ozui-serial-col">#</th>
                                    <th className="ozui-name-col">
                                        <FontAwesomeIcon icon={faCar} />
                                        {translations.carName || 'Car Name'} <span className="required">*</span>
                                    </th>
                                    <th className="ozui-date-col">
                                        <FontAwesomeIcon icon={faCalendar} />
                                        {translations.date || 'Date'}
                                    </th>
                                    <th className="ozui-action-col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cars.map((car, index) => (
                                    <tr key={car.id} className="ozui-car-row">
                                        <td className="ozui-serial-cell">
                                            <span className="ozui-row-number">{index + 1}</span>
                                        </td>
                                        <td className="ozui-name-cell">
                                            <input
                                                type="text"
                                                className="ozui-input"
                                                value={car.car_name}
                                                onChange={(e) => updateCarField(car.id, 'car_name', e.target.value)}
                                                placeholder={`${translations.carName || 'Car Name'} *`}
                                                disabled={loading}
                                            />
                                        </td>
                                        <td className="ozui-date-cell">
                                            <input
                                                type="date"
                                                className="ozui-input"
                                                value={car.date}
                                                onChange={(e) => updateCarField(car.id, 'date', e.target.value)}
                                                disabled={loading}
                                            />
                                        </td>
                                        <td className="ozui-action-cell">
                                            <button
                                                type="button"
                                                className="ozui-remove-btn"
                                                onClick={() => removeCarEntry(car.id)}
                                                disabled={loading}
                                                title={translations.remove || 'Remove'}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                <div className="ozui-add-row-container">
                    <button
                        type="button"
                        className="ozui-add-row-btn"
                        onClick={addCarEntry}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations.addCar || 'Add Car'}</span>
                    </button>
                </div>
                
                {duplicateSerials.length > 0 && (
                    <div className="ozui-warning-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>{translations.duplicateSerial || 'Duplicate serial number detected'}: {duplicateSerials.join(', ')}</span>
                    </div>
                )}
                
                {cars.length === 1 && !cars[0].car_name && !cars[0].serial_number && (
                    <div className="ozui-empty-state">
                        <FontAwesomeIcon icon={faCar} />
                        <p>{translations.clickToAdd || 'Click + to add cars'}</p>
                    </div>
                )}
                
                <div className="ozui-form-actions">
                    <button
                        type="button"
                        className="ozui-btn ozui-btn-primary"
                        onClick={saveAllCars}
                        disabled={loading || totalCars === 0}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{translations.saveAll || 'Save All Cars'}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CarForm;